/* eslint-disable sonarjs/cognitive-complexity */
import { Stack } from 'component-library';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { BenchmarkType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useMatchCurrentPage } from '@/hooks/useMatchCurrentPage';
import { usePathSet } from '@/hooks/usePathSet';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';
import { formatUnit, unitToTonne } from '@/utils/formatting';
import { printMonthYear } from '@/utils/formatting/date';

import { Chart, getXAxisProps, Line, LineChart, TooltipProps } from './components/Chart';
import { CHART_ZOOM, ChartZoomStateHook } from './components/ChartZoom';
import { ChartLegendBar } from './components/LegendBar';
import { RenderBenchmarkReferenceLine } from './components/ReferenceLine';
import { ChartTooltip, ChartTooltipTextPrimary } from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useAggregationBenchmarks } from './hooks/useAggregationBenchmarks';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { GraphFact } from './types';
import { getBenchmarkLabel, getDataDefaultDomain, kgToTonneFormatter, minMaxAxisDomain } from './utils';
import { getAggregationGraphColor } from './utils/getAggregationGraphColor';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const interpolateColor = (light: string, dark: string, tint: number) => {
  const parseRGBA = (rgba: string) => {
    const rgb = rgba.match(/\d+/g)?.map(Number);
    if (!rgb || rgb.length < 3) return [0, 0, 0];
    return rgb.slice(0, 3);
  };
  const start = parseRGBA(light);
  const end = parseRGBA(dark);
  // eslint-disable-next-line sonarjs/no-nested-template-literals
  return `rgba(${start.map((color, index) => Math.round(color + ((end[`${index}`] ?? 0) - color) * tint)).join(',')},1)`;
};

const FORMATTERS = {
  [GraphTileType.historic_below_ground_carbon]: kgToTonneFormatter,
  [GraphTileType.historic_above_ground_carbon]: kgToTonneFormatter,
};

const GRAPH_KEYS = {
  [GraphTileType.historic_below_ground_carbon]: 'permanent_soil_carbon',
  [GraphTileType.historic_above_ground_carbon]: 'above_ground_carbon',
  [GraphTileType.historic_water_soil_moisture]: 'water_holding_capacity',
  [GraphTileType.protected_on_farm_habitat]: 'biodiversity_percent',
  [GraphTileType.social_life_expectancy]: 'social_life_expectancy',
  [GraphTileType.ndvi]: 'vegetation_condition_ndvi',
  [GraphTileType.historic_habitat_intactness]: 'habitat_intactness_total',
  [GraphTileType.historic_habitat_intactness_threats_breakdown]: 'threat_breakdown_total',
  [GraphTileType.historic_deforestation]: 'deforestation_total',
};

export type ScalarGraphPoint = GraphFact & {
  id: string;
};

export type Timeseries = {
  id: string;
  name: string;
  value: ScalarGraphPoint[];
  unit: string;
};

type ItemizedTimeseriesChartProps = {
  data: Timeseries[];
  indicator: GraphTileType;
  unit: UnitEnum;
  benchmarks?: ReturnType<typeof useAggregationBenchmarks> | null;
  isPlotCountLimited: boolean;
  yAxisLabel?: string;
  customBenchmark?: CustomBenchmarkSuccessResponse | null;
};

export const ItemizedTimeseriesChart: FC<ItemizedTimeseriesChartProps & Partial<ChartZoomStateHook>> = ({
  data,
  indicator,
  unit,
  benchmarks,
  zoom,
  setZoom,
  isPlotCountLimited,
  yAxisLabel,
  customBenchmark,
  ...delegated
}) => {
  const { t } = useTranslation();
  const pathSet = usePathSet();

  const isPageWithPlotsTable = useMatchCurrentPage([
    pathSet.carbonDetails,
    pathSet.waterDetails,
    pathSet.biodiversityDetails,
    pathSet.soilDetails,
    pathSet.socialDetails,
    pathSet.plots,
  ]);

  const chartContainerRef = useRef<HTMLDivElement>(null);

  const yDomainData = useMemo(() => data.flatMap((plot) => plot.value), [data]);

  const formatter = FORMATTERS[indicator as keyof typeof FORMATTERS];

  const defaultDomain = getDataDefaultDomain(yDomainData, 0.8, 1.2, formatter);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);

  if (data.length === 0) {
    Logger.error(`Not enough data to render chart (indicator: ${indicator})`);
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const allDates = Array.from(
    new Set(
      data.flatMap((plot) =>
        Array.isArray(plot.value)
          ? plot.value
              .filter((point) => point.name.includes(GRAPH_KEYS[indicator as keyof typeof GRAPH_KEYS]))
              .map((point) => point.date)
          : [],
      ),
    ),
  ).sort();

  const chartData = allDates.map((date) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entry: Record<string, any> = { date: new Date(date).getTime() };
    data.forEach((plot) => {
      const point = plot.value.find(
        (p) => p.date === date && p.name.includes(GRAPH_KEYS[indicator as keyof typeof GRAPH_KEYS]),
      );
      const formattedValue = formatter ? formatter(point as GraphFact) : point?.value;
      entry[plot.name] = formattedValue ?? null;
    });
    return entry;
  });
  const firstTick = chartData.at(0);
  const lastTick = chartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const chartConfig = {
    value: {
      color: '',
    },
  };
  const xTicksDomain = [new Date(firstTick.date).getFullYear(), new Date(lastTick.date).getFullYear()];
  const xAxisProps = {
    ...getXAxisProps(xTicksDomain),
    tickFormatter: printMonthYear,
    dataKey: 'date',
  };

  const benchmarkHighFact = Array.isArray(benchmarks?.high?.value)
    ? benchmarks?.high?.value?.at(0)
    : (benchmarks?.high as PlotReportFactElement<number | null>);
  const benchmarkModerateFact = Array.isArray(benchmarks?.moderate?.value)
    ? benchmarks?.moderate?.value?.at(0)
    : (benchmarks?.moderate as PlotReportFactElement<number | null>);
  const benchmarkLowFact = Array.isArray(benchmarks?.low?.value)
    ? benchmarks?.low?.value?.at(0)
    : (benchmarks?.low as PlotReportFactElement<number | null>);
  const benchmarkVeryLowFact = Array.isArray(benchmarks?.very_low?.value)
    ? benchmarks?.very_low?.value?.at(0)
    : (benchmarks?.very_low as PlotReportFactElement<number | null>);
  const benchmarkUnchangedFact = Array.isArray(benchmarks?.unchanged?.value)
    ? benchmarks?.unchanged?.value?.at(0)
    : (benchmarks?.unchanged as PlotReportFactElement<number | null>);

  const benchmarkValues = customBenchmark
    ? {
        high: customBenchmark.high_value ?? undefined,
        moderate: customBenchmark.moderate_value ?? undefined,
        low: customBenchmark.low_value ?? undefined,
      }
    : {
        high: formatter ? formatter(benchmarkHighFact) : benchmarkHighFact?.value,
        moderate: formatter ? formatter(benchmarkModerateFact) : benchmarkModerateFact?.value,
        low: formatter ? formatter(benchmarkLowFact) : benchmarkLowFact?.value,
        very_low: formatter ? formatter(benchmarkVeryLowFact) : benchmarkVeryLowFact?.value,
        unchanged: formatter ? formatter(benchmarkUnchangedFact) : benchmarkUnchangedFact?.value,
      };

  const baseColors = getAggregationGraphColor(indicator);

  const plotColors = data.reduce(
    (acc, plot, idx) => {
      let strokeColor = ``;
      if (baseColors) {
        const tint = data.length > 1 ? idx / (data.length - 1) : 0;
        strokeColor = interpolateColor(baseColors.light, baseColors.dark, tint);
      }
      acc[plot.name] = strokeColor;
      return acc;
    },
    {} as Record<string, string>,
  );

  const formattedUnit = (indicator as keyof typeof FORMATTERS) ? formatUnit(unitToTonne(unit)) : formatUnit(unit);

  const hasBenchmarkZoom =
    zoom === CHART_ZOOM.BENCHMARK && !isNullish(benchmarkValues?.low) && !isNullish(benchmarkValues?.high);

  const yAxisDomainFromButtons = hasBenchmarkZoom
    ? // We use directly the benchmark values because the aggregated fact data does not contain the benchmark facts
      [benchmarkValues.low, benchmarkValues.high]
    : getChartZoomedDomain({
        data: yDomainData,
        zoom,
        defaultDomain: minMaxAxisDomain(0.8, 1.2),
        formatter,
      });

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        config={chartConfig}
        data={chartData}
        data-testid='aggregation-historic-graph'
      >
        <LineChart data={chartData}>
          {data.map((plot) => {
            return (
              <Line
                key={plot.name}
                dataKey={plot.name}
                stroke={plotColors[plot.name]}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
            );
          })}
          {benchmarkValues.high &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.high,
              benchmark: BenchmarkType.high,
              yAxisDomain: yAxisDomain as [number, number],
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.high'), customBenchmark?.name),
            })}
          {benchmarkValues.moderate &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.moderate,
              benchmark: BenchmarkType.moderate,
              yAxisDomain: yAxisDomain as [number, number],
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.moderate'), customBenchmark?.name),
            })}
          {benchmarkValues.low &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.low,
              benchmark: BenchmarkType.low,
              yAxisDomain: yAxisDomain as [number, number],
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.low'), customBenchmark?.name),
            })}
          {benchmarkValues?.very_low &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.very_low,
              benchmark: BenchmarkType.very_low,
              yAxisDomain: yAxisDomain as [number, number],
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.very_low'), customBenchmark?.name),
            })}
          {benchmarkValues?.unchanged &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.unchanged,
              benchmark: BenchmarkType.unchanged,
              yAxisDomain: yAxisDomain as [number, number],
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.unchanged'), customBenchmark?.name),
            })}
          <Chart.XAxis {...xAxisProps} className='aggregated-line-graph-x-axis' />
          <Chart.YAxis domain={yAxisDomain} className='aggregated-bar-graph-y-axis' allowDataOverflow={true}>
            <Chart.Label>{formattedUnit || `${yAxisLabel}`}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip
            content={(props) => <CustomTooltip {...props} unit={formattedUnit} plotColors={plotColors} />}
            cursor={false}
          />
        </LineChart>
      </Chart.Container>
      <CustomLegend data={data} plotColors={plotColors} />
      {isPlotCountLimited && (
        <span className='typography-caption text-text-secondary m-2 ml-7'>
          <i className='ri-information-line mr-2' />
          {t(isPageWithPlotsTable ? 'graph.chartStyle.plotLimitWarningPlotTable' : 'graph.chartStyle.plotLimitWarning')}
        </span>
      )}
    </>
  );
};

const CustomLegend: FC<{ data: Timeseries[]; plotColors: Record<string, string> }> = ({ data, plotColors }) => {
  const legendBarItems: ChartLegendBarItem[] = data.map((plot) => ({
    label: plot.name,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    color: plotColors[plot.name!]!,
    shape: 'area' as const,
  }));

  return <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' />;
};

const CustomTooltip = ({
  active,
  payload,
  unit,
  plotColors,
}: TooltipProps<ValueType, NameType> & {
  unit: string | undefined;
  plotColors: Record<string, string>;
}) => {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;

  const dateLabel = payload[0]?.payload?.date ? printMonthYear(payload[0].payload.date) : '';

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='w-full justify-between'>
        <ChartTooltipTextPrimary>{dateLabel}</ChartTooltipTextPrimary>
      </Stack>
      <Stack spacing={1} direction='col' className='w-full'>
        {payload
          ?.sort((a, b) => ((a.value ?? 0) > (b.value ?? 0) ? -1 : 1))
          .map((plot) => {
            const plotName = plot.name || plot.dataKey;
            const { value } = plot;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const strokeColor = plotColors[plotName!]!;

            const factValue = !isNullish(value)
              ? `${getDisplayNumber(value as unknown as string, window.navigator.language)} ${unit}`
              : t('global.analysis.noData');
            return (
              <Stack key={plotName} direction='row' className='gap-4 w-full flex justify-between'>
                <Stack direction='row' className='items-center gap-2'>
                  <div style={{ backgroundColor: strokeColor }} className='w-2 h-2 rounded-full flex-shrink-0' />
                  <ChartTooltipTextPrimary>{plotName}</ChartTooltipTextPrimary>
                </Stack>
                <ChartTooltipTextPrimary>{factValue}</ChartTooltipTextPrimary>
              </Stack>
            );
          })}
      </Stack>
    </ChartTooltip>
  );
};
